import { useEffect } from "react";
import { useLayerStructuralActions } from "../components/MainVidPlayer/useLayerStructuralActions";
import type { LayerSortAction } from "../components/MainVidPlayer/useLayerStructuralActions";
import { useMst } from "../stores/main";
import { isKeyboardShortcutSuppressedTarget } from "./keyboardShortcutGuards";

function isPrimaryShortcutModifier(e: KeyboardEvent): boolean {
  return e.metaKey || e.ctrlKey;
}

function isLeftBracket(e: KeyboardEvent): boolean {
  return e.key === "[" || e.code === "BracketLeft";
}

function isRightBracket(e: KeyboardEvent): boolean {
  return e.key === "]" || e.code === "BracketRight";
}

export function getLayerSortShortcutAction(
  e: KeyboardEvent
): LayerSortAction | undefined {
  if (!isPrimaryShortcutModifier(e) || e.altKey) {
    return undefined;
  }

  if (isRightBracket(e)) {
    return e.shiftKey ? "bringToFront" : "bringForward";
  }

  if (isLeftBracket(e)) {
    return e.shiftKey ? "sendToBack" : "sendBackward";
  }

  return undefined;
}

/**
 * Hook that sets up keyboard shortcuts for layer sorting:
 * - Cmd/Ctrl+] brings selected layers forward
 * - Cmd/Ctrl+Shift+] brings selected layers to front
 * - Cmd/Ctrl+[ sends selected layers backward
 * - Cmd/Ctrl+Shift+[ sends selected layers to back
 */
export function useLayerSortingShortcuts() {
  const { currentTab, playerStore } = useMst();
  const { sortLayers } = useLayerStructuralActions();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const action = getLayerSortShortcutAction(e);
      if (e.repeat || !action || isKeyboardShortcutSuppressedTarget(e.target)) {
        return;
      }

      if (
        currentTab !== "layers" ||
        !playerStore.layerInspectorEnabled ||
        playerStore.layerInspectorMode !== "design" ||
        !playerStore.layerInspectorSelections.length
      ) {
        return;
      }

      e.preventDefault();
      sortLayers(action);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [currentTab, playerStore, sortLayers]);
}
